import React from 'react';
import { UserRoundIcon, BrainCircuitIcon, DatabaseIcon, CloudIcon, SettingsIcon, CheckIcon } from 'lucide-react';
import { approachVisualLabels, type ApproachVisualId } from '../../data/approachSteps';
type Props = {
  isFR: boolean;
};
const pick = (isFR: boolean, value: {
  en: string;
  fr: string;
}) => isFR ? value.fr : value.en;

/** 01 — a laptop and phone, suggesting every entry point. */
function EntryPointsVisual() {
  return <div className="flex items-end justify-center gap-2" aria-hidden="true" data-id="element-555" data-mp="3si1kiv481rnx">
      <div className="flex-1 max-w-[150px]" data-id="element-556" data-mp="3nnnkgfem6wcx">
        <div className="rounded-t-md border border-[#DFE2EB] bg-white overflow-hidden" data-id="element-557" data-mp="3jndeh2qto5y2">
          <div className="h-2.5 border-b border-[#DFE2EB] bg-[#F4F5F9]" data-id="element-558" data-mp="3eoilj7z55108" />
          <div className="flex gap-1.5 p-1.5" data-id="element-559" data-mp="3eqd62l6i2r3s">
            <div className="flex-1 space-y-1" data-id="element-560" data-mp="39pywgyp9393n">
              <div className="h-1.5 w-4/5 rounded bg-[#C8CCD8]" data-id="element-561" data-mp="39onu93bge9en" />
              <div className="h-1 w-3/5 rounded bg-[#DFE2EB]" data-id="element-562" data-mp="34rdwn0dwurfq" />
              <div className="h-2.5 w-8 rounded bg-[#4F46E5]/25 mt-1.5" data-id="element-563" data-mp="3c4a575p78l7h" />
            </div>
            <div className="w-10 h-10 rounded bg-[#4F46E5]/12" data-id="element-564" data-mp="3c2ffle2zg4x2" />
          </div>
        </div>
        <div className="h-1 rounded-b bg-[#DFE2EB]" data-id="element-565" data-mp="3gwtgtx4bt69n" />
      </div>
      <div className="w-8 rounded-md border border-[#DFE2EB] bg-white p-1 space-y-1" data-id="element-566" data-mp="327f36ctpw2og">
        <div className="h-0.5 w-3 rounded bg-[#DFE2EB] mx-auto" data-id="element-567" data-mp="3768j0bwh85rs" />
        <div className="h-4 rounded bg-[#4F46E5]/12" data-id="element-568" data-mp="3uv193rqf3jeh" />
        <div className="h-0.5 rounded bg-[#DFE2EB]" data-id="element-569" data-mp="3uvb2zqel2apg" />
        <div className="h-0.5 w-2/3 rounded bg-[#DFE2EB]" data-id="element-570" data-mp="3p05u5cp1u3e" />
      </div>
    </div>;
}

/** 02 — verified identity chip, sits on the indigo card. */
function IdentityVisual({
  isFR
}: Props) {
  return <div className="rounded-xl bg-white px-3 py-3 flex items-center gap-2.5" data-id="element-571" data-mp="3pv5dogvsd0aj">
      <span className="w-8 h-8 shrink-0 rounded-full bg-[#4F46E5]/[0.08] flex items-center justify-center" data-id="element-572" data-mp="29heyof34orm4">
        <UserRoundIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-573" data-mp="2eg9t6eagk4m2" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-574" data-mp="kx9gxpr372ac">
        <div className="font-sans text-xs text-[#121212] leading-tight" data-id="element-575" data-mp="fyppxaxmz0b4">
          {pick(isFR, approachVisualLabels.identityCard.name)}
        </div>
        <div className="mt-1.5 space-y-1" aria-hidden="true" data-id="element-576" data-mp="b6oic2bexxrf">
          <div className="h-1 w-full rounded bg-[#E4E6EC]" data-id="element-577" data-mp="69emo4xfkpv5" />
          <div className="h-1 w-2/3 rounded bg-[#E4E6EC]" data-id="element-578" data-mp="66sgo9q6uif1" />
        </div>
      </div>
      <span className="w-5 h-5 shrink-0 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-579" data-mp="1bd0vc8mecfz">
        <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-580" data-mp="3tcazin4cm3xy" />
      </span>
    </div>;
}

/** 03 — orchestration, not a chat thread. */
function AtnaVisual({
  isFR
}: Props) {
  return <div className="rounded-xl border border-[#E4E6EC] bg-[#FAFAFB] px-3 py-3 flex items-center gap-2.5" data-id="element-581" data-mp="3jgzl65wuqyqq">
      <span className="w-8 h-8 shrink-0 rounded-full bg-[#4F46E5]/[0.08] flex items-center justify-center" data-id="element-582" data-mp="1mmd6uy4bf4uc">
        <BrainCircuitIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-583" data-mp="1hlywvaj5lkto" />
      </span>
      <div className="min-w-0" data-id="element-584" data-mp="1u5vuxisf1o9s">
        <div className="font-sans text-xs text-[#121212] leading-snug" data-id="element-585" data-mp="1u52v71rvjz15">
          {pick(isFR, approachVisualLabels.atnaCaption)}
        </div>
        <div className="mt-1.5 space-y-1" aria-hidden="true" data-id="element-586" data-mp="23w7dobgre20q">
          <div className="h-1 w-16 rounded bg-[#E4E6EC]" data-id="element-587" data-mp="23vp5cggtnxcg" />
        </div>
      </div>
    </div>;
}

/** 04 — the connected systems behind the experience. */
function SystemsVisual() {
  const icons = [DatabaseIcon, CloudIcon, SettingsIcon];
  return <div className="flex items-center gap-2" aria-hidden="true" data-id="element-588" data-mp="1g6d0ttwfwdgh">
      {icons.map((Icon, index) => <span key={index} className="flex-1 h-11 rounded-xl border border-[#E4E6EC] bg-white flex items-center justify-center" data-id="element-589" data-mp="1l6957u91u6wk" data-mpi={index}>
          <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} data-id="element-590" data-mp="2roe3errzbar6" />
        </span>)}
    </div>;
}

/** 05 — the completed outcome. */
function ActionVisual({
  isFR
}: Props) {
  return <div className="rounded-xl border border-[#E4E6EC] bg-[#FAFAFB] px-3 py-3 flex items-center gap-2.5" data-id="element-591" data-mp="2u8k9p642fdaw">
      <span className="w-7 h-7 shrink-0 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-592" data-mp="2q78qpjfp5qbo">
        <CheckIcon className="w-4 h-4 text-white" strokeWidth={2.75} data-id="element-593" data-mp="2z5kdfadg03yu" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-594" data-mp="2z7nf4rnyjlje">
        <div className="font-sans text-xs text-[#121212] leading-tight" data-id="element-595" data-mp="3d4tro4i38o06">
          {pick(isFR, approachVisualLabels.actionDone)}
        </div>
        <div className="mt-1.5 space-y-1" aria-hidden="true" data-id="element-596" data-mp="3938iou8octv9">
          <div className="h-1 w-full rounded bg-[#E4E6EC]" data-id="element-597" data-mp="3mveopni45kgz" />
          <div className="h-1 w-1/2 rounded bg-[#E4E6EC]" data-id="element-598" data-mp="3mwzivc0in1eb" />
        </div>
      </div>
    </div>;
}
export function StepVisual({
  visual,
  isFR
}: {
  visual: ApproachVisualId;
  isFR: boolean;
}) {
  switch (visual) {
    case 'entrypoints':
      return <EntryPointsVisual data-id="element-599" data-mp="3rvaxe01ra02v" />;
    case 'identity':
      return <IdentityVisual isFR={isFR} data-id="element-600" data-mp="fanjzz25qlz5" />;
    case 'atna':
      return <AtnaVisual isFR={isFR} data-id="element-601" data-mp="fb5d4mtiwk2u" />;
    case 'systems':
      return <SystemsVisual data-id="element-602" data-mp="jcqxsucau2nl" />;
    case 'action':
      return <ActionVisual isFR={isFR} data-id="element-603" data-mp="jbfsur74aouw" />;
    default:
      return null;
  }
}